/* The one place the viewer writes. A chart is saved into a collection of the user's own:
   the active one when it is theirs, and otherwise — the base is read-only — the collection
   named in the edit bar, which then becomes the active one so that what is on screen is what
   was saved. Nothing is kept in the browser, and the base is never written. */

import { freshName, type Collection } from "@preflop/api";
import type { Spot } from "@preflop/core";
import { switchCollection } from "../../state/session";
import { saveSetting } from "../../state/settings";
import { page, reloadSpots } from "./state";

/** The target that is not a collection yet: a new copy of what is on screen. */
export const NEW_COPY = "new";
export const SAVE_TARGET_SETTING = "saveTarget";
/** The spot a reload should open on, handed across it. */
export const OPEN_SPOT = "openSpot";

/** Whether saves go straight into the active collection: it is the user's own, or it is the
    base and the user is one of its administrators. */
export function activeIsOwn(): boolean {
  const { collection, user, isAdmin } = page().session;
  return collection.isBase ? isAdmin : collection.ownerId === user.id;
}

/** The user's collections a chart could be saved into from a collection that is not theirs. */
export function saveTargets(): Collection[] {
  const { collections, user } = page().session;
  return collections.filter((c) => !c.isBase && c.ownerId === user.id);
}

/** The target as the edit bar should show it: the remembered one if it still exists. */
export function currentTarget(): string {
  const p = page();
  return saveTargets().some((c) => c.id === p.target) ? p.target : NEW_COPY;
}

export function chooseTarget(target: string): void {
  page().target = target;
  void saveSetting(SAVE_TARGET_SETTING, target);
}

/** Make a collection active and open it on a spot. The page is on its way out after this. */
async function leaveFor(collectionId: string, key: string): Promise<"left"> {
  sessionStorage.setItem(OPEN_SPOT, key);
  await switchCollection(page().session.user.id, { id: collectionId, isBase: false });
  location.reload();
  return "left";
}

/**
 * Save a chart. "saved": it is in the active collection and the page's list is rebuilt.
 * "left": it went into another collection and the page is reloading onto it. "cancelled":
 * the target already held that spot and the user kept it; nothing was written.
 * Throws what the database or the network refused; the caller shows it and keeps the draft.
 */
export async function saveChart(spot: Spot): Promise<"saved" | "left" | "cancelled"> {
  const p = page();
  const { api, collection, collections } = p.session;

  if (activeIsOwn()) {
    const done = await api.saveSpot(collection.id, spot);
    if (done.status === "exists") return "cancelled"; // not reachable: "replace" is the default
    const at = p.session.spots.findIndex((s) => s.key === done.spot.key);
    if (at >= 0) p.session.spots[at] = done.spot;
    else p.session.spots.push(done.spot);
    reloadSpots();
    return "saved";
  }

  // Out of a collection that is not the user's: the chart remembers the row it started from.
  const copiedFrom = spot.id ?? null;
  const target = currentTarget();
  if (target === NEW_COPY) {
    const name = freshName(
      collections.map((c) => c.name),
      "My ranges",
    );
    const id = await api.copyCollection(collection.id, name);
    await api.saveSpot(id, spot, { copiedFrom });
    return leaveFor(id, spot.key);
  }

  const name = collections.find((c) => c.id === target)?.name ?? "that collection";
  const first = await api.saveSpot(target, spot, { copiedFrom, ifExists: "refuse" });
  if (first.status === "exists") {
    // Its chart may differ from the one this edit started from: ask before it is replaced.
    if (!confirm(`"${name}" already has a chart for this spot. Replace it with this one?`))
      return "cancelled";
    await api.saveSpot(target, spot, { copiedFrom });
  }
  return leaveFor(target, spot.key);
}

/** Remove a chart from the active collection, which must be the user's own. */
export async function deleteChart(key: string): Promise<void> {
  const p = page();
  await p.session.api.deleteSpot(p.session.collection.id, key);
  p.session.spots = p.session.spots.filter((s) => s.key !== key);
  reloadSpots();
}
